import { useRef, useEffect, useState, useCallback } from 'react';
import { VideoJSPlayerProps, ProgressData, VideoJSPlayer } from '../types';
import { useDeviceDetection } from './useDeviceDetection';
import { createVideoJSConfig } from '../core/VideoJSConfig';
import videojs from '../videojs-imports';

// VideoJSのグローバル宣言
declare global {
  interface Window {
    videojs: any;
  }
}

export const useVideoJS = (props: VideoJSPlayerProps) => {
  const {
    videoId,
    videoUrl,
    initialTime = 0,
    autoPlay = false,
    muted = false,
    quality = 'auto',
    useStreaming = true,
    liveMode = false,
    onReady,
    onProgress,
    onDuration,
    onEnded,
    onError,
    onAutoPlayError
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<VideoJSPlayer | null>(null);
  const initializationRef = useRef<boolean>(false);
  const retryCountRef = useRef<number>(0);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const deviceInfo = useDeviceDetection();

  // 最大再試行回数
  const MAX_RETRY_COUNT = 10;

  // タイマーをクリアする関数
  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // イベントハンドラー設定
  const setupEventHandlers = useCallback((player: VideoJSPlayer) => {
    // 再生進捗
    player.on('timeupdate', () => {
      if (onProgress) {
        const currentTime = player.currentTime();
        const duration = player.duration() || 0;
        const buffered = player.buffered();
        const bufferedEnd = buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;

        const progressData: ProgressData = {
          currentTime: currentTime || 0,
          duration,
          played: duration > 0 ? (currentTime || 0) / duration : 0,
          loaded: duration > 0 ? bufferedEnd / duration : 0,
          buffered: bufferedEnd
        };

        onProgress(progressData);
      }
    });

    // 動画の長さが判明した時
    player.on('durationchange', () => {
      if (onDuration && player) {
        try {
          const duration = (player as any).duration();
          // Infinityや無効な値をチェック
          if (duration && !isNaN(duration) && isFinite(duration) && duration > 0) {
            onDuration(duration);
          } else {
            console.warn('Invalid duration detected:', duration);
          }
        } catch (e) {
          console.warn('Duration取得エラー:', e);
        }
      }
    });

    // メタデータが読み込まれた時にも長さをチェック
    player.on('loadedmetadata', () => {
      if (onDuration && player) {
        try {
          const duration = (player as any).duration();
          if (duration && !isNaN(duration) && isFinite(duration) && duration > 0) {
            onDuration(duration);
          }
        } catch (e) {
          console.warn('Metadata duration取得エラー:', e);
        }
      }
    });

    // 再生終了
    player.on('ended', () => {
      if (onEnded) {
        onEnded();
      }
    });

    // エラー処理
    player.on('error', () => {
      const playerError = player.error();
      console.error('VideoJSプレイヤーエラー:', playerError);

      // コード4は MEDIA_ERR_SRC_NOT_SUPPORTED（ソース非対応）であり自動再生エラーではない
      // 自動再生ブロックは play() の Promise rejection (NotAllowedError) として来る
      if (onError) {
        onError(playerError);
      }

      setError(new Error(playerError?.message || 'ビデオプレイヤーエラーが発生しました'));
    });

  }, [onProgress, onDuration, onEnded, onError, onAutoPlayError]);

  // VideoJS初期化関数
  const initializePlayer = useCallback(() => {
    // 既に初期化済みまたは再試行回数上限に達した場合はスキップ
    if (initializationRef.current || retryCountRef.current >= MAX_RETRY_COUNT) {
      if (retryCountRef.current >= MAX_RETRY_COUNT) {
        console.error('VideoJS初期化の最大再試行回数に達しました');
        setError(new Error('VideoJS初期化の最大再試行回数に達しました'));
      }
      return;
    }

    // video要素の存在確認
    if (!videoRef.current || !videoUrl) {
      return;
    }

    // DOM要素がDOMツリーに含まれているかチェック
    if (!document.contains(videoRef.current)) {
      retryCountRef.current++;
      console.warn(`Video element is not in DOM yet, retry ${retryCountRef.current}/${MAX_RETRY_COUNT}`);
      
      if (retryCountRef.current < MAX_RETRY_COUNT) {
        clearRetryTimer();
        retryTimerRef.current = setTimeout(() => {
          initializePlayer();
        }, 100);
      }
      return;
    }

    // 既存のプレイヤーがある場合は破棄
    if (playerRef.current) {
      try {
        playerRef.current.dispose();
      } catch (error) {
        console.warn('Player disposal error:', error);
      }
      playerRef.current = null;
    }

    // DOM要素がすでにVideoJSプレイヤーとして初期化されている場合は既存のプレイヤーを取得
    if (videoRef.current.hasAttribute('data-vjs-player')) {
      console.warn('Element already initialized as VideoJS player, attempting to reuse');
      try {
        // 既存のプレイヤーを取得
        const existingPlayer = videojs.getPlayer(videoRef.current);
        if (existingPlayer) {
          playerRef.current = existingPlayer as any;
          setIsReady(true);
          setError(null);
          
          // イベントハンドラー再登録
          setupEventHandlers(existingPlayer as any);
          
          // 初期再生位置設定
          if (initialTime > 0) {
            existingPlayer.currentTime(initialTime);
          }
          
          return;
        }
      } catch (error) {
        console.warn('Failed to reuse existing player, disposing and recreating:', error);
        // 既存のプレイヤーを強制的に破棄
        try {
          const existingPlayer = videojs.getPlayer(videoRef.current);
          if (existingPlayer) {
            (existingPlayer as any).dispose();
          }
        } catch (disposeError) {
          console.warn('Failed to dispose existing player:', disposeError);
        }
        
        // data-vjs-player属性を削除
        videoRef.current.removeAttribute('data-vjs-player');
        videoRef.current.className = videoRef.current.className.replace(/vjs-\S+/g, '').trim();
      }
    }

    try {
      initializationRef.current = true;

      // VideoJS設定作成
      const config = createVideoJSConfig({
        videoId,
        videoUrl,
        deviceInfo,
        autoPlay,
        muted,
        quality,
        useStreaming,
        liveMode,
      });

      // VideoJSプレイヤー初期化
      const player = videojs(videoRef.current, config) as any;
      playerRef.current = player;

      // ローディングスピナーを明示的に無効化
      try {
        const loadingSpinner = player.el().querySelector('.vjs-loading-spinner') as HTMLElement;
        if (loadingSpinner) {
          loadingSpinner.style.display = 'none';
          loadingSpinner.style.visibility = 'hidden';
          loadingSpinner.style.opacity = '0';
        }
      } catch (error) {
        console.warn('Failed to hide loading spinner:', error);
      }

      // プレイヤー準備完了時の処理
      player.ready(() => {
        setIsReady(true);
        setError(null);

        // 初期再生位置設定
        if (initialTime > 0) {
          player.currentTime(initialTime);
        }

        // onReadyコールバックはVideoJSPlayerコンポーネント側で処理
      });

      // イベントハンドラー登録
      setupEventHandlers(player);

    } catch (err) {
      console.error('VideoJS初期化エラー:', err);
      setError(err instanceof Error ? err : new Error('VideoJS初期化に失敗しました'));
      initializationRef.current = false;
    }
  }, [videoId, videoUrl, deviceInfo, autoPlay, muted, quality, useStreaming, liveMode, initialTime, setupEventHandlers, clearRetryTimer]);

  // VideoJS初期化
  useEffect(() => {
    if (!videoRef.current || !videoUrl) {
      return;
    }

    // 初期化状態をリセット
    initializationRef.current = false;
    retryCountRef.current = 0;
    clearRetryTimer();

    // 少し遅延してから初期化を実行（DOM準備を待つ）
    const timer = setTimeout(() => {
      initializePlayer();
    }, 50);

    return () => {
      clearTimeout(timer);
      clearRetryTimer();

      if (playerRef.current) {
        try {
          playerRef.current.dispose();
        } catch (error) {
          console.warn('Player cleanup error:', error);
        }
        playerRef.current = null;
      }

      // プレイヤーを捨てたので未準備に戻す。
      //
      // ここを落とすと、動画を切り替えても isReady が true のまま変化しないため、
      // VideoJSPlayer 側のイベント購読 useEffect（deps に isReady を持つ）が
      // React の bail out で再実行されず、**新しいプレイヤーにハンドラが一切
      // 張られない**。旧プレイヤー（dispose 済み）に残ったまま解除もされない。
      // 症状は「関連動画をクリックすると再生/一時停止・時刻・進捗が固まる」で、
      // 動画から動画へ遷移したときだけ起きる（一覧経由では再マウントされるので出ない）。
      setIsReady(false);

      initializationRef.current = false;
      retryCountRef.current = 0;
    };
  }, [videoId, videoUrl]); // 依存関係を最小限に

  // リロード等の初期化レースで、プレイヤーが空ソースのまま準備完了することがある。
  // その場合に確定済みの videoUrl を再適用して再生できるようにする自己修復処理。
  // 通常はソース設定済みのため何もしない（happy path に影響なし）。
  // videoId も依存に含め、動画切替時に古いタイマーが残らないようにする。
  useEffect(() => {
    if (!isReady || !videoUrl) return;

    const detectType = (url: string): string => {
      const u = url.toLowerCase();
      if (u.includes('.m3u8') || u.includes('hls')) return 'application/x-mpegURL';
      if (u.includes('.webm')) return 'video/webm';
      return 'video/mp4';
    };

    // 通常のソース読み込みを待ってから、まだ未設定なら再適用する
    const timer = setTimeout(() => {
      // タイマー発火時点の最新プレイヤーを取得（再初期化で差し替わっている可能性があるため）
      const player = playerRef.current as (VideoJSPlayer & {
        currentSrc?: () => string;
        readyState?: () => number;
        isDisposed?: () => boolean;
        src?: (source: { src: string; type: string }) => void;
      }) | null;
      // 破棄済み・未初期化なら何もしない
      if (!player) return;
      if (typeof player.isDisposed === 'function' && player.isDisposed()) return;

      try {
        const current = typeof player.currentSrc === 'function' ? player.currentSrc() : '';
        const readyState = typeof player.readyState === 'function' ? player.readyState() : 0;
        // ソース未設定かつ読み込みが始まっていない場合のみ再適用する
        // （HLS 等で一時的に currentSrc が空になるケースを readyState で除外）
        if (!current && readyState === 0 && typeof player.src === 'function') {
          player.src({ src: videoUrl, type: detectType(videoUrl) });
          const p = player.play();
          if (p && typeof p.catch === 'function') {
            p.catch((err: unknown) => {
              // autoplay ブロック（NotAllowedError）等は想定内。それ以外のみ警告する。
              const name = err && typeof err === 'object' ? (err as { name?: string }).name : '';
              if (name !== 'NotAllowedError' && name !== 'AbortError') {
                console.warn('デモ動画の再生に失敗:', err);
              }
            });
          }
        }
      } catch (e) {
        console.warn('ソース再適用に失敗:', e);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [isReady, videoId, videoUrl, autoPlay]);

  // プレイヤー制御メソッド
  const controls = {
    play: () => {
      if (playerRef.current) {
        return playerRef.current.play();
      }
      return Promise.reject(new Error('プレイヤーが初期化されていません'));
    },
    
    pause: () => {
      if (playerRef.current) {
        playerRef.current.pause();
      }
    },
    
    seek: (time: number) => {
      if (playerRef.current) {
        playerRef.current.currentTime(time);
      }
    },
    
    setVolume: (volume: number) => {
      if (playerRef.current) {
        playerRef.current.volume(Math.max(0, Math.min(1, volume)));
      }
    },
    
    setMuted: (muted: boolean) => {
      if (playerRef.current) {
        playerRef.current.muted(muted);
      }
    },
    
    setPlaybackRate: (rate: number) => {
      if (playerRef.current) {
        playerRef.current.playbackRate(rate);
      }
    },
    
    getCurrentTime: () => {
      return playerRef.current ? playerRef.current.currentTime() : 0;
    },
    
    getDuration: () => {
      return playerRef.current ? playerRef.current.duration() : 0;
    },
    
    isPaused: () => {
      return playerRef.current ? playerRef.current.paused() : true;
    },
    
    isEnded: () => {
      return playerRef.current ? playerRef.current.ended() : false;
    }
  };

  return {
    videoRef,
    playerRef,
    isReady,
    error,
    deviceInfo,
    controls
  };
};